import { Component } from '@angular/core';

import {
    ActionSheetButton,
    ActionSheetController,
    Platform,
    ToastController,
} from '@ionic/angular';
import { Router } from '@angular/router';
import { GlobalService } from './global.service';
import { I18nService } from './shared/i18n.service';
import { AjaxService } from './ajax.service';
import { SentryService } from './services/sentry.service';
import { PerformanceMonitorService } from './services/performance-monitor.service';

import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false,
})
export class AppComponent {
    public appPages = [
        {
            title_orig: 'Artikel info',
            title: this.i18n.tr('Artikel info'),
            url: '/home',
            icon: 'home',
            hide: false,
        },
        {
            title_orig: 'Afboeking',
            title: this.i18n.tr('Afboeking'),
            url: '/afboeking',
            icon: 'cart',
            hide: false,
        },
        {
            title_orig: 'Afboeking +/-',
            title: this.i18n.tr('Afboeking') + ' +/-',
            url: '/afboeking2',
            icon: 'barcode-outline',
            hide: false,
        },
        {
            title_orig: 'Locatieverplaatsing',
            title: this.i18n.tr('Locatieverplaatsing'),
            url: '/locatieverplaatsing',
            icon: 'swap-horizontal-outline',
            hide: false,
        },
        {
            title_orig: 'Overboeking',
            title: this.i18n.tr('Overboeking'),
            url: '/overboeking',
            icon: 'code-working',
            hide: false,
        },
        {
            title_orig: 'Voorraad',
            title: this.i18n.tr('Voorraad'),
            url: '/voorraadmutatie',
            icon: 'grid-outline',
            hide: false,
        },
        {
            title_orig: 'Kaart',
            title: this.i18n.tr('Kaart'),
            url: '/map',
            icon: 'globe-outline',
            hide: false,
        },
        {
            title_orig: 'Bedrijven',
            title: this.i18n.tr('Bedrijven'),
            url: '/bedrijven',
            icon: 'business-outline',
            hide: false,
        },
        {
            title_orig: 'Ontvangst',
            title: this.i18n.tr('Ontvangst'),
            url: '/te-ontvangen',
            icon: 'cube-outline',
            hide: false,
        },
        {
            title_orig: 'Objecten',
            title: this.i18n.tr('Objecten'),
            url: '/object',
            icon: 'albums-outline',
            hide: false,
        },
        {
            title_orig: 'Orders',
            title: this.i18n.tr('Orders'),
            url: '/orders',
            icon: 'clipboard-outline',
            hide: false,
        },
        // ? calendar not used untill finished implemenation of aantek with google callendar and microsoft callendar
        {
            title: '[en]Calendar',
            title_orig: this.i18n.tr('[en]Calendar'),
            url: '/calendar',
            icon: 'calendar-outline',
            hide: !this.global.showCalendar,
        },
        {
            title_orig: 'Uren',
            title: this.i18n.tr('Uren'),
            url: '/uren',
            icon: 'time-outline',
            hide: false,
        },
        {
            title_orig: '[en]Settings',
            title: this.i18n.tr('[en]Settings'),
            url: '/settings',
            icon: 'settings-outline',
            hide: false,
        },
        {
            title_orig: '[en]About',
            title: this.i18n.tr('[en]About'),
            url: '/about',
            icon: 'information-circle',
            hide: false,
        },
        {
            title_orig: '[en]Logout',
            title: this.i18n.tr('[en]Logout'),
            click: () => this.logout(),
            icon: 'exit',
            hide: false,
        },
    ];
    // voor icons zie https://ionicons.com/

    constructor(
        private platform: Platform,
        public global: GlobalService,
        public i18n: I18nService,
        private router: Router,
        private ajax: AjaxService,
        private actionSheetCtrl: ActionSheetController,
        private toastCtrl: ToastController,
        private sentry: SentryService,
        private performanceMonitor: PerformanceMonitorService
    ) {
        this.initializeApp();
    }

    initializeApp() {
        this.platform.ready().then(async () => {
            if (this.platform.is('capacitor')) {
                if (Capacitor.getPlatform() === 'web') {
                    try {
                        await this.sentry.initSentry();
                        console.log('Sentry initialized successfully');

                        this.performanceMonitor.monitorPageLoad();
                        this.performanceMonitor.monitorFrameRate();
                        console.log('Performance monitoring initialized');
                    } catch (error) {
                        console.error('Failed to initialize Sentry:', error);
                    }
                }

                const info = await Device.getInfo();

                // Keep classic insets on Android to avoid OEM keyboard inset issues.
                if (info.platform === 'android') {
                    try {
                        await EdgeToEdge.disable();
                        await EdgeToEdge.setBackgroundColor({
                            color: '#FFFFFF',
                        });
                    } catch (error) {
                        console.warn(
                            'Failed to configure EdgeToEdge on Android:',
                            error
                        );
                    }
                }
            }
        });

        setInterval(() => {
            if (this.global.isLoggedIn) {
                this.ajax
                    .get('ping')
                    .then((dataping: any) => {
                        if (dataping && dataping.msg === 'LOGOUT') {
                            console.log('Server requested logout via ping.');
                            this.logout();
                        }
                    })
                    .catch((error) => {
                        console.error(
                            'Ping request failed. User might be offline.',
                            error
                        );
                    });
            }
        }, 30000);
    }

    async logout() {
        console.log('app.component/logout: user:', this.global.username);
        try {
            await this.ajax.get('logout', {
                compname: this.global.compname,
                username: this.global.username,
                password: this.global.password,
                pcnr: this.global.uuid,
            });
        } catch (error) {
            console.error(
                'Backend logout call failed, proceeding with client-side logout:',
                error
            );
        } finally {
            await this.global.logout();
            this.router.navigateByUrl('/login');
        }
    }

    async openProfileManager() {
        const buttons: ActionSheetButton[] = this.global.profiles.map(
            (profile) => {
                return {
                    text: `${profile.username} @ ${profile.company}`,
                    icon: 'trash',
                    role: 'destructive',
                    handler: () => {
                        this.global.removeProfile(profile);
                        const message =
                            this.i18n.tr('[en]Profile for') +
                            ` ${profile.username} ` +
                            this.i18n.tr('[en]was removed.');
                        this.presentToast(message, 'primary');
                    },
                };
            }
        );

        buttons.push({
            text: this.i18n.tr('[en]Cancel'),
            icon: 'close',
            role: 'cancel',
        });

        const actionSheet = await this.actionSheetCtrl.create({
            header: this.i18n.tr('[en]Delete a Profile'),
            buttons: buttons,
        });
        await actionSheet.present();
    }

    async openProfileSwitcher() {
        const buttons: ActionSheetButton[] = this.global.profiles.map(
            (profile) => {
                return {
                    text: `${profile.username} @ ${profile.company}`,
                    icon:
                        this.global.username === profile.username &&
                        this.global.compname === profile.company
                            ? 'person-circle'
                            : 'person-circle-outline',
                    handler: () => {
                        if (
                            this.global.username !== profile.username ||
                            this.global.compname !== profile.company
                        ) {
                            this.global.switchProfile(profile);
                            this.router.navigateByUrl('/login', {
                                state: { isSwitching: true },
                            });
                        }
                    },
                };
            }
        );

        buttons.push({
            text: this.i18n.tr('[en]Add Profiles'),
            icon: 'add-circle-outline',
            handler: () => {
                this.global.prepareToAddProfile();
                this.router.navigateByUrl('/login', {
                    state: { isAddingProfile: true },
                });
            },
        });

        if (this.global.profiles.length > 0) {
            buttons.push({
                text: this.i18n.tr('[en]Delete Profile'),
                icon: 'trash-outline',
                handler: () => {
                    this.openProfileManager();
                },
            });
        }

        buttons.push({
            text: this.i18n.tr('[en]Cancel'),
            icon: 'close',
            role: 'cancel',
        });

        const actionSheet = await this.actionSheetCtrl.create({
            header: this.i18n.tr('[en]Switch Profile'),
            buttons: buttons,
        });
        await actionSheet.present();
    }

    async presentToast(message: string, color: string) {
        const toast = await this.toastCtrl.create({
            message,
            duration: 3000,
            color,
            position: 'bottom',
        });
        toast.present();
    }
}
